import { useLocation } from 'react-router-dom';
import { MenuListNew, MenuListFlat } from '../constants/NavBar.jsx';
import { useMenu } from '../context/MenuContext';
import {
  MastersList,
  DELETE_ALLOWED_ROLES,
  EDIT_ALLOWED_ROLES,
  READONLY_ROLES,
  localStorageKeyName,
  AuthConstant
} from '../constants/constant.js';
import { useTheme, useMediaQuery } from '@mui/material';
import dayjs from 'dayjs';

export const useResponsive = () => {
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  return { isMobile, isTablet, isDesktop };
};

export function useMasterId() {
  const location = useLocation();
  for (const menu of MenuListNew) {
    // Check if the menu itself matches the path
    if (menu.to === location.pathname) {
      return menu.id ?? 0;
    }

    // If the menu has content, check child menus
    if (menu.content && menu.content.length > 0) {
      const subMenus = menu.content.find((sub) => sub.to === location.pathname);
      if (subMenus) {
        return subMenus.id ?? 0;
      }
    }
  }

  return null;
}

export function getAccessConfig() {
  const location = useLocation();
  for (const menu of MenuListNew) {
    // Check if the menu itself matches the path
    if (menu.to === location.pathname) {
      return menu ?? 0;
    }

    // If the menu has content, check child menus
    if (menu.content && menu.content.length > 0) {
      const subMenus = menu.content.find((sub) => sub.to === location.pathname);
      if (subMenus) {
        return subMenus ?? 0;
      }
    }
  }

  return null;
}

export function useMasterData() {
  const masterId = useMasterId();

  if (!masterId) return null;
  const mastersMenu = MastersList[masterId];
  return mastersMenu;
}

export const handleDaysCoversion = (startDate, endDate) => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  const days = end.diff(start, 'day');
  return days;
};

export const isDeleteAllowed = () => {
  if (DELETE_ALLOWED_ROLES.length === 0) {
    return false;
  } else if (DELETE_ALLOWED_ROLES.length === 1) {
    if (DELETE_ALLOWED_ROLES[0] === AuthConstant.ALL) {
      return true;
    }
  }
  const userDetails = JSON.parse(localStorage.getItem(localStorageKeyName));
  return DELETE_ALLOWED_ROLES.includes(userDetails.role) ? true : false;
};

export const isEditAllowed = () => {
  if (EDIT_ALLOWED_ROLES.length === 0) {
    return true;
  } else if (EDIT_ALLOWED_ROLES.length === 1) {
    if (EDIT_ALLOWED_ROLES[0] === AuthConstant.ALL) {
      return true;
    }
  }
  const userDetails = JSON.parse(localStorage.getItem(localStorageKeyName));
  return EDIT_ALLOWED_ROLES.includes(userDetails.role) ? true : false;
};

export const isAddAllowed = () => {
  return isEditAllowed();
};

export const isReadonlyMode = () => {
  if (READONLY_ROLES.length === 0) {
    return false;
  } else if (READONLY_ROLES.length === 1) {
    if (READONLY_ROLES[0] === AuthConstant.ALL) {
      return true;
    }
  }
  const userDetails = JSON.parse(localStorage.getItem(localStorageKeyName));
  return READONLY_ROLES.includes(userDetails.role) ? true : false;
};

export const generate12DigitAlphaNumericCode = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let code = '';

  for (let i = 0; i < 12; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }

  return code;
};

export const isDcrScreen = () => {
  const location = useLocation();
  const dcrPaths = ['/dcr-list', '/dcr-clients', '/dcr-city', '/dcr-area'];
  return dcrPaths.includes(location.pathname);
};

export const userScreenAccess = (path = null) => {
  const location = path ? { pathname: path } : useLocation();
  const { getMenuList } = useMenu();
  // Search for matching menu item in MenuListFlat from context
  const menuListFlat = getMenuList();
  for (const menu of menuListFlat) {
    if (menu.subMenus && menu.subMenus.length > 0) {
      for (const subMenus of menu.subMenus) {
        if (subMenus.routingKey === location.pathname) {
          return {
            hasListPermission: subMenus.hasListPermission,
            hasAddPermission: subMenus.hasAddPermission,
            hasEditPermission: subMenus.hasEditPermission,
            hasDeletePermission: subMenus.hasDeletePermission
          };
        }
      }
    }
  }

  // Return default permissions if route not found
  return {
    hasListPermission: false,
    hasAddPermission: false,
    hasEditPermission: false,
    hasDeletePermission: false,
    IsAssigned: false
  };
};

export const getScreenAccess = () => {
  const location = useLocation();
  const { getMenuList } = useMenu();

  // Search for matching menu item in MenuListFlat from context
  const menuListFlat = getMenuList();
  for (const menu of menuListFlat) {
    if (menu.subMenus && menu.subMenus.length > 0) {
      for (const subMenus of menu.subMenus) {
        if (subMenus.routingKey === location.pathname) {
          return {
            hasListPermission: subMenus.hasListPermission,
            hasAddPermission: subMenus.hasAddPermission,
            hasEditPermission: subMenus.hasEditPermission,
            hasDeletePermission: subMenus.hasDeletePermission
          };
        }
      }
    }
  }

  // Return default permissions if route not found
  return {
    hasListPermission: false,
    hasAddPermission: false,
    hasEditPermission: false,
    hasDeletePermission: false,
    IsAssigned: false
  };
};

export const useUserHasPermission = () => {
  const location = useLocation();
  const { getMenuList } = useMenu();

  // Search for matching menu item in MenuListFlat from context
  const menuListFlat = getMenuList();
  for (const menu of menuListFlat) {
    if (menu.subMenus && menu.subMenus.length > 0) {
      for (const subMenus of menu.subMenus) {
        if (subMenus.routingKey === location.pathname) {
          return subMenus.hasListPermission;
        }
      }
    }
  }

  // Return default permissions if route not found
  return false;
};

export const IsAdmins = () => {
  const { getAdmins } = useMenu();
  const admins = getAdmins();
  const userDetails = JSON.parse(localStorage.getItem(localStorageKeyName));
  return admins.includes(userDetails?.role);
};

export const formatDate = (date) => {
  if (!date) return null;
  return dayjs(date).format('YYYY-MM-DD');
};

export const formatDateTime = (date) => {
  if (!date) return null;
  return dayjs(date).format('YYYY-MM-DDTHH:mm:ss');
};